import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';

import { StyledLink } from '@selfserve/react-ui';
import { IIndexSignature, IRoutePath } from '@selfserve/react-utils';

const StyledNavigationContainer = styled('div')(
  ({ theme }: { theme?: any }) => ({
    position: 'relative',
    width: '100%',
    height: '100%',
    maxWidth: '280px',
    borderRight: `1px solid ${theme.palette.grey[400]}`,
  })
);

const StyledNavigationMenuContainer = styled('div')(
  ({ theme }: { theme?: any }) => ({
    position: 'sticky',
    top: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '36px 0 0 26px',
  })
);

const StyledAdminLink = styled(StyledLink, {
  shouldForwardProp: (prop) => {
    return (
      prop !== 'isActive' &&
      prop !== 'isDropdownItem' &&
      prop !== 'isDropdown' &&
      prop !== 'isExpanded'
    );
  },
})(
  ({
    theme,
    isActive,
    isDropdown,
    isDropdownItem,
    isExpanded,
  }: {
    theme?: any;
    isActive?: boolean;
    isDropdown?: boolean;
    isDropdownItem?: boolean;
    isExpanded?: boolean;
  }) => ({
    position: 'relative',
    marginBottom: !isDropdown ? '30px' : '18px',
    fontWeight: '700',
    color: isActive ? theme.palette.primary.main : theme.palette.grey['100'],
    ...(isDropdown && {
      '&::after': {
        content: '""',
        position: 'absolute',
        background: 'no-repeat url("assets/dropdown-arrow.svg") center',
        width: '8px',
        height: '5px',
        right: '12px',
        top: '50%',
        marginTop: '-2.5px',
        ...(isExpanded && {
          transform: 'rotate(180deg)',
        }),
      },
      ...(!isExpanded && {
        margin: 0,
      }),
    }),
    ...(isDropdownItem && {
      margin: 0,
      padding: '8px 0 8px 46px',
      borderLeft: `1px solid ${theme.palette.grey['500']}`,
      ...(isActive && {
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        paddingLeft: '44px',
      }),
    }),
  })
);

const StyledAdminDropdownContainer = styled('div')(
  ({ theme }: { theme?: any }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '30px',
  })
);

interface Props {
  currentPath: string;
  paths: IRoutePath[];
}

const AdminNavigation = ({ paths, currentPath }: Props) => {
  const { search } = useLocation();
  const { productFamily } = useParams();
  const [expandedIds, setExpandedIds] = useState<IIndexSignature<boolean>>({});

  useEffect(() => {
    const currentRoute = paths.find(({ children }) =>
      children?.find(({ id }) => id === productFamily)
    );
    if (currentRoute) {
      setExpandedIds({ [currentRoute.id as string]: true });
    }
  }, [paths]);

  const handleToggleDropdown = useCallback(
    (id: string) => (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      setExpandedIds((prevExpandedIds) => ({
        ...prevExpandedIds,
        [id]: !prevExpandedIds[id],
      }));
    },
    []
  );

  return (
    <StyledNavigationContainer>
      <StyledNavigationMenuContainer>
        {paths.map(({ path, name, id, children }: IRoutePath) => {
          const isActive = currentPath === path;
          const isExpanded = !!expandedIds[id as string];
          return !children?.length ? (
            <StyledAdminLink
              to={path as string}
              key={id}
              isActive={isActive}
              disabled={!path && !children?.length}
            >
              {name}
            </StyledAdminLink>
          ) : (
            <StyledAdminDropdownContainer key={id}>
              <StyledAdminLink
                onClick={handleToggleDropdown(id as string)}
                isExpanded={isExpanded}
                isDropdown
                to=""
              >
                {name}
              </StyledAdminLink>
              {isExpanded &&
                children.map(
                  ({ path: childPath, name: childName, id: childId }) => {
                    const isChildActive =
                      currentPath === childPath || productFamily === childName;
                    return (
                      childId && (
                        <StyledAdminLink
                          to={[childPath as string, search].join('')}
                          key={childId}
                          isActive={isChildActive}
                          isDropdownItem
                        >
                          {childName}
                        </StyledAdminLink>
                      )
                    );
                  }
                )}
            </StyledAdminDropdownContainer>
          );
        })}
      </StyledNavigationMenuContainer>
    </StyledNavigationContainer>
  );
};

export { AdminNavigation };
